/**
 * SpecialtyForumPage — View all discussions in a specific specialty forum
 *
 * Displays threads using the same card design as LitDigests/LitHub.
 * Enforces access control: users can only view forums for their digest specialties.
 * Pro Trial: full access, Non-trial: read-only.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { discussionsAPI } from '../api';
import { toast } from 'sonner';
import {
  MessageSquare,
  Search,
  ChevronLeft,
  Plus,
  Clock,
  ChevronRight,
  Eye,
  Lock,
  Users,
  Sparkles,
  BookOpen,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type { DiscussionThread } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';

const CARD_BG = [
  'bg-white',
  'bg-slate-50/70',
  'bg-gray-50/60',
  'bg-zinc-50/50',
];

// ============================================================================
// THREAD CARD COMPONENT
// ============================================================================
interface ThreadCardProps {
  thread: DiscussionThread;
  index: number;
  onView: (threadId: string) => void;
}

const ThreadCard: React.FC<ThreadCardProps> = ({ thread, index, onView }) => {
  const [expanded, setExpanded] = useState(false);
  const shade = CARD_BG[index % CARD_BG.length];

  const formatTimeAgo = (dateString: string | null | undefined): string => {
    if (!dateString) return 'No activity';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const hasPreview = thread.preview_comment && thread.preview_comment.length > 20;
  const needsExpand = thread.preview_comment && thread.preview_comment.length > 150;
  const displayPreview = expanded
    ? thread.preview_comment
    : thread.preview_comment?.slice(0, 150) + (needsExpand ? '...' : '');

  return (
    <div
      className={`rounded-xl border transition-all shadow-sm hover:shadow-md ${shade} border-slate-200/80 hover:border-sky-200`}
      data-testid={`thread-card-${thread.thread_id}`}
    >
      {/* ─── Row 1: Title + Metadata ─── */}
      <div className="px-5 pt-4 pb-2">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {thread.is_pinned && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Pinned</Badge>
              )}
              <h3
                className="responsive-title text-base font-semibold text-slate-900 hover:text-sky-700 cursor-pointer transition-colors line-clamp-2"
                onClick={() => onView(thread.thread_id)}
                data-testid={`thread-title-${thread.thread_id}`}
              >
                {thread.title}
              </h3>
            </div>

            <div className="flex items-center gap-2 mt-1.5 flex-wrap text-sm">
              <span className="text-slate-600">
                By {thread.creator_name || 'Anonymous'}
              </span>
              {thread.creator_is_verified && (
                <Badge variant="secondary" className="h-4 px-1 text-[9px] font-medium bg-emerald-100 text-emerald-700 border-0">
                  Verified
                </Badge>
              )}
              <span className="text-slate-400">·</span>
              <span className="text-slate-500 flex items-center gap-1">
                <MessageSquare className="h-3.5 w-3.5" />
                {thread.comment_count} comments
              </span>
              <span className="text-slate-400">·</span>
              <span className="text-slate-500 flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {formatTimeAgo(thread.last_activity_at)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Row 2: Preview Comment ─── */}
      {hasPreview && (
        <div className="px-5 py-2.5 border-t border-slate-100 bg-sky-50/30">
          <button
            onClick={() => needsExpand && setExpanded(!expanded)}
            className="flex items-center gap-1.5 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-sky-500 hover:text-sky-700 transition-colors"
            data-testid={`toggle-preview-${thread.thread_id}`}
          >
            <Sparkles className="h-3 w-3" /> Latest Comment
            {needsExpand && (
              expanded
                ? <ChevronUp className="h-3 w-3" />
                : <ChevronDown className="h-3 w-3" />
            )}
          </button>
          <p className="text-sm text-slate-700 leading-relaxed">
            {displayPreview}
          </p>
        </div>
      )}

      {/* ─── Row 3: Actions ─── */}
      <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-100 rounded-b-xl">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onView(thread.thread_id)}
          className="h-8 px-3 text-xs text-sky-600 border-sky-200 hover:bg-sky-50"
          data-testid={`view-thread-btn-${thread.thread_id}`}
        >
          View Discussion
          <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const SpecialtyForumPage: React.FC = () => {
  const navigate = useNavigate();
  const { specialtyId } = useParams<{ specialtyId: string }>();
  const { user } = useAuth();
  const { flags, ready: flagsReady } = useFeatureFlags();

  // State
  const [threads, setThreads] = useState<DiscussionThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [specialtyName, setSpecialtyName] = useState('');
  const [canPost, setCanPost] = useState(false);
  const [isFrozen, setIsFrozen] = useState(false);

  // Determine if user has active trial
  const hasActiveTrial = useMemo(() => {
    if (!user) return false;
    const trialEnds = user.trial_ends_at || user.trial_expires_at;
    if (!trialEnds) return false;
    try {
      const endDate = new Date(trialEnds);
      return endDate > new Date();
    } catch {
      return false;
    }
  }, [user]);

  // Load threads
  useEffect(() => {
    if (!flagsReady || !specialtyId) return;

    const loadThreads = async () => {
      setLoading(true);
      setError(null);
      try {
        // First get room info
        const roomsData = await discussionsAPI.getSpecialtyRooms();
        const room = roomsData.rooms.find((r: any) => r.specialty_id === specialtyId) as any;
        if (room) {
          setSpecialtyName(room.specialty_name);
          setCanPost(room.can_post && hasActiveTrial);
          setIsFrozen(room.access_state === 'frozen');
        }

        // Get threads
        const data = await discussionsAPI.getSpecialtyThreads(specialtyId);
        setThreads(data.threads);
      } catch (err: any) {
        const code = err?.response?.data?.detail?.error_code;
        if (code === 'community_locked') {
          setError('Create a digest in this specialty to access this forum.');
        } else {
          setError('Failed to load forum threads');
          toast.error('Failed to load forum');
        }
      } finally {
        setLoading(false);
      }
    };

    loadThreads();
  }, [flagsReady, specialtyId, hasActiveTrial]);

  // Filter threads
  const filteredThreads = useMemo(() => {
    if (!searchQuery.trim()) return threads;
    const query = searchQuery.toLowerCase();
    return threads.filter(
      (thread) =>
        thread.title.toLowerCase().includes(query) ||
        thread.creator_name?.toLowerCase().includes(query) ||
        thread.preview_comment?.toLowerCase().includes(query)
    );
  }, [threads, searchQuery]);

  // Handlers
  const handleViewThread = (threadId: string) => {
    navigate(`/community/thread/${threadId}`);
  };

  const handleNewThread = () => {
    if (!canPost && !hasActiveTrial) {
      toast.error('Upgrade to Pro to start discussions');
      return;
    }
    navigate(`/community/new?specialty=${specialtyId}`);
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50/50 via-slate-50 to-white py-8" data-testid="specialty-forum-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-96" />
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-5 bg-white">
                <Skeleton className="h-5 w-3/4 mb-3" />
                <Skeleton className="h-4 w-1/2 mb-2" />
                <Skeleton className="h-16 w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50/50 via-slate-50 to-white py-8" data-testid="specialty-forum-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/community')}
            className="mb-4"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to LitClub
          </Button>
          <Card className="max-w-md mx-auto">
            <CardContent className="py-12 text-center">
              <Lock className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Forum Locked</h2>
              <p className="text-slate-600 mb-6">{error}</p>
              <Button onClick={() => navigate('/preferences')}>
                Go to Preferences
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/50 via-slate-50 to-white py-8" data-testid="specialty-forum-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/community')}
            className="mb-3"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to LitClub
          </Button>

          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                {specialtyName || 'Forum'}
                {isFrozen && (
                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                    <Eye className="h-3 w-3 mr-1" />
                    Read-only
                  </Badge>
                )}
              </h1>
              <p className="text-slate-600 mt-1">
                {threads.length} discussion{threads.length !== 1 ? 's' : ''} in this forum
              </p>
            </div>
            {canPost && !isFrozen && (
              <Button
                onClick={handleNewThread}
                className="bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="new-thread-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Discussion
              </Button>
            )}
          </div>

          {/* Frozen notice */}
          {isFrozen && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
              <Eye className="h-5 w-5 text-amber-600" />
              <p className="text-sm text-amber-800">
                Your access to this forum is read-only. Create a new digest in this specialty to regain full access.
              </p>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search discussions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="thread-search-input"
            />
          </div>
        </div>

        {/* Threads List */}
        <ScrollArea className="h-[calc(100vh-320px)]">
          {filteredThreads.length > 0 ? (
            <div className="space-y-4 pr-2" data-testid="threads-list">
              {filteredThreads.map((thread, index) => (
                <ThreadCard
                  key={thread.thread_id}
                  thread={thread}
                  index={index}
                  onView={handleViewThread}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16" data-testid="no-threads">
              {searchQuery ? (
                <>
                  <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No matching discussions</h3>
                  <p className="text-slate-500">Try different search terms</p>
                </>
              ) : (
                <>
                  <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No discussions yet</h3>
                  <p className="text-slate-500 mb-4">Be the first to start a conversation!</p>
                  {canPost && !isFrozen && (
                    <Button onClick={handleNewThread}>
                      Start Discussion
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
};

export default SpecialtyForumPage;
